import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  Modal,
  Nav,
  Spinner,
  Table,
} from 'react-bootstrap';
import AdminSidebar from '../../components/AdminSidebar';
import {
  AdminNotificationsProvider,
  useAdminNotifications,
} from '../../components/AdminNotifications';
import { buildApiUrl } from '../../config/apiConfig';
import useAdminGuard from '../../hooks/useAdminGuard';
import { hasPermission, PERMISSION_IDS } from '../../utils/permissions';
import './AdminUsuariosPage.css';
import { logError, warn } from '../../utils/logger';

const AdminUsuariosPageInner = () => {
  const { user, token } = useAdminGuard();
  const navigate = useNavigate();
  const { notify } = useAdminNotifications();

  const canViewUsers = useMemo(() => hasPermission(user, PERMISSION_IDS.VER_USUARIOS), [user]);

  useEffect(() => {
    if (user && !canViewUsers) {
      notify({
        title: 'Acceso denegado',
        message: 'No tienes permisos para gestionar usuarios.',
        variant: 'error',
      });
      navigate('/403', { replace: true });
    }
  }, [user, canViewUsers, navigate, notify]);

  // Estados de usuarios
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [selectedUsers, setSelectedUsers] = useState([]);

  // Estados de estadísticas
  const [stats, setStats] = useState({
    totalUsers: 0,
    adminUsers: 0,
    onlineUsers: 0,
  });

  // Estados de roles (solo para el select del modal)
  const [roles, setRoles] = useState([]);

  // Estados de modales
  const [showViewUserModal, setShowViewUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewModalTab, setViewModalTab] = useState('perfil');
  const [adminActivity, setAdminActivity] = useState(null);
  const [adminActivityLoading, setAdminActivityLoading] = useState(false);
  const [adminActivityError, setAdminActivityError] = useState(null);
  const [resendingOrderId, setResendingOrderId] = useState(null);

  // Estados de formularios
  const [userForm, setUserForm] = useState({
    nombre: '',
    apellido: '',
    email: '',
    role: '',
    status: 'active',
  });

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    }),
    [token],
  );

  const loadUserAdminActivity = useCallback(
    async (userId) => {
      if (!token || !userId) return;
      setAdminActivityLoading(true);
      setAdminActivityError(null);
      try {
        const response = await fetch(buildApiUrl(`/api/admin/users/${userId}/activity`), {
          headers: authHeaders,
        });
        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(errBody.error || `Error ${response.status}`);
        }
        const data = await response.json();
        setAdminActivity(data);
      } catch (error) {
        logError('[AdminUsuarios] admin user activity:', error);
        setAdminActivityError(error.message || 'No se pudo cargar la actividad del usuario');
      } finally {
        setAdminActivityLoading(false);
      }
    },
    [token, authHeaders],
  );

  const closeViewUserModal = useCallback(() => {
    setShowViewUserModal(false);
    setAdminActivity(null);
    setAdminActivityError(null);
    setViewModalTab('perfil');
  }, []);

  // Cargar estadísticas de usuarios conectados
  const loadOnlineStats = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(buildApiUrl('/api/online/stats'), {
        headers: authHeaders,
      });
      if (!response.ok) {
        warn('[AdminUsuarios] Error obteniendo estadísticas online:', response.status);
        return;
      }
      const data = await response.json();
      setStats((prevStats) => ({
        ...prevStats,
        onlineUsers: data.totalOnline || 0,
      }));
    } catch (error) {
      logError('[AdminUsuarios] Error cargando estadísticas online:', error);
      // No mostrar notificación para evitar spam, solo loguear el error
    }
  }, [token, authHeaders]);

  // Cargar usuarios
  const loadUsers = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await fetch(buildApiUrl('/api/user'), {
        headers: authHeaders,
      });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const data = await response.json();
      const usersList = (data.users || []).map((u) => ({
        ...u,
        name: u.nombre || u.name || '',
        lastName: u.apellido || u.lastName || '',
        status: u.status || (u.activo ? 'active' : 'inactive'),
        role: u.role?.nombre || u.rol?.nombre || u.role || u.rol || '',
      }));
      setUsers(usersList);
      setStats((prevStats) => ({
        totalUsers: usersList.length,
        adminUsers: usersList.filter((u) => {
          const role = typeof u.role === 'object' ? u.role?.nombre : u.role || u.rol?.nombre || u.rol || '';
          const roleName = role.toString().toLowerCase();
          return roleName.includes('admin');
        }).length,
        onlineUsers: prevStats.onlineUsers, // Mantener el valor actual mientras se carga
      }));
      // Cargar estadísticas de usuarios conectados después de cargar usuarios
      await loadOnlineStats();
    } catch (error) {
      logError('[AdminUsuarios] Error loading users:', error);
      notify({
        title: 'Error al cargar usuarios',
        message: 'No se pudieron cargar los usuarios.',
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [token, authHeaders, notify, loadOnlineStats]);

  // Cargar roles (solo para el select del modal)
  const loadRoles = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(buildApiUrl('/api/roles'), {
        headers: authHeaders,
      });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const data = await response.json();
      setRoles(data.roles || []);
    } catch (error) {
      logError('[AdminUsuarios] Error loading roles:', error);
    }
  }, [token, authHeaders]);

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, [loadUsers, loadRoles]);

  // Actualizar estadísticas de usuarios conectados periódicamente (cada 10 segundos)
  useEffect(() => {
    if (!token) return;

    // Cargar inmediatamente
    loadOnlineStats();

    // Configurar intervalo para actualizar cada 10 segundos
    const intervalId = setInterval(() => {
      loadOnlineStats();
    }, 10000); // 10 segundos

    // Limpiar intervalo al desmontar
    return () => {
      clearInterval(intervalId);
    };
  }, [token, loadOnlineStats]);

  // Filtrar usuarios
  useEffect(() => {
    let filtered = [...users];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          (u.name || u.nombre || '').toLowerCase().includes(term) ||
          (u.email || '').toLowerCase().includes(term) ||
          String(u.id || '').includes(term),
      );
    }

    if (roleFilter) {
      filtered = filtered.filter((u) => {
        const userRole = typeof u.role === 'object' ? u.role?.nombre : u.role || u.rol?.nombre || u.rol || '';
        return userRole.toLowerCase() === roleFilter.toLowerCase();
      });
    }

    if (statusFilter) {
      filtered = filtered.filter((u) => {
        const status = (u.status || u.estado || 'active').toLowerCase();
        return status === statusFilter.toLowerCase();
      });
    }

    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [users, searchTerm, roleFilter, statusFilter]);

  // Paginación
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * usersPerPage;
    return filteredUsers.slice(start, start + usersPerPage);
  }, [filteredUsers, currentPage, usersPerPage]);

  // Handlers de usuarios
  const handleViewUser = (userRow) => {
    setSelectedUser(userRow);
    setViewModalTab('perfil');
    setAdminActivity(null);
    setAdminActivityError(null);
    setShowViewUserModal(true);
    loadUserAdminActivity(userRow.id);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    const roleName = typeof user.role === 'object' ? user.role?.nombre : user.role || user.rol?.nombre || user.rol || '';
    setUserForm({
      nombre: user.name || user.nombre || '',
      apellido: user.lastName || user.apellido || '',
      email: user.email || '',
      role: roleName,
      status: user.status || (user.activo ? 'active' : 'inactive') || 'active',
    });
    setShowEditUserModal(true);
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;
    try {
      // Buscar el ID del rol si se proporcionó un nombre
      let roleId = null;
      if (userForm.role && userForm.role.trim() !== '') {
        const selectedRole = roles.find((r) => r.nombre === userForm.role);
        if (selectedRole) {
          roleId = Number(selectedRole.id);
        }
      }

      const payload = {
        nombre: userForm.nombre,
        apellido: userForm.apellido,
        email: userForm.email,
      };

      // Actualizar rol si se proporcionó
      if (roleId && !isNaN(roleId)) {
        const roleResponse = await fetch(buildApiUrl(`/api/user/${selectedUser.id}/role`), {
          method: 'PATCH',
          headers: authHeaders,
          body: JSON.stringify({ rolId: roleId }), // Usar snake_case como espera el servidor
        });
        if (!roleResponse.ok) {
          const errorData = await roleResponse.json().catch(() => ({ error: 'Error desconocido' }));
          throw new Error(errorData.error || `Error al actualizar el rol (${roleResponse.status})`);
        }
      }

      // Actualizar estado si cambió
      const currentStatus = selectedUser.status || (selectedUser.activo ? 'active' : 'inactive');
      if (userForm.status !== currentStatus) {
        const statusValue = userForm.status === 'active';
        const statusResponse = await fetch(buildApiUrl(`/api/user/${selectedUser.id}/status`), {
          method: 'PATCH',
          headers: authHeaders,
          body: JSON.stringify({ activo: statusValue }),
        });
        if (!statusResponse.ok) {
          throw new Error('Error al actualizar el estado');
        }
      }

      // Actualizar datos básicos
      const response = await fetch(buildApiUrl(`/api/user/${selectedUser.id}`), {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      notify({
        title: 'Usuario actualizado',
        message: 'El usuario se actualizó correctamente.',
        variant: 'success',
      });
      setShowEditUserModal(false);
      loadUsers();
    } catch (error) {
      logError('[AdminUsuarios] Error saving user:', error);
      notify({
        title: 'Error al guardar',
        message: error.message || 'No se pudo actualizar el usuario.',
        variant: 'error',
      });
    }
  };

  const getRoleDisplayName = (role) => {
    if (!role) return 'Sin rol';
    if (typeof role === 'object') {
      return role.nombre || 'Sin rol';
    }
    const roleName = role.toString();
    return roleName.charAt(0).toUpperCase() + roleName.slice(1).toLowerCase();
  };

  const getStatusBadge = (status) => {
    const statusValue = (status || 'active').toLowerCase();
    if (statusValue === 'active' || statusValue === 'activo') {
      return <Badge bg="success">Activo</Badge>;
    }
    if (statusValue === 'inactive' || statusValue === 'inactivo') {
      return <Badge bg="secondary">Inactivo</Badge>;
    }
    if (statusValue === 'pending' || statusValue === 'pendiente') {
      return <Badge bg="warning">Pendiente</Badge>;
    }
    return <Badge bg="danger">Suspendido</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Fecha inválida';
    }
  };

  const formatCurrency = (amount, currency = 'ARS') => {
    if (amount == null || amount === '') return '—';
    const n = Number(amount);
    if (Number.isNaN(n)) return String(amount);
    try {
      return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: currency || 'ARS',
        maximumFractionDigits: 2,
      }).format(n);
    } catch {
      return `${currency} ${n}`;
    }
  };

  const orderStatusLabel = (status) => {
    const map = {
      pending: 'Pendiente',
      processing: 'En proceso',
      paid: 'Pagado',
      completed: 'Completado',
      canceled: 'Cancelado',
      refunded: 'Reembolsado',
      failed: 'Fallido',
    };
    return map[status] || status || '—';
  };

  const orderStatusVariant = (status) => {
    if (status === 'paid' || status === 'completed') return 'success';
    if (status === 'pending' || status === 'processing') return 'warning';
    if (status === 'canceled' || status === 'refunded' || status === 'failed') return 'secondary';
    return 'info';
  };

  const canResendPurchaseConfirmation = (order) =>
    order && (order.estado === 'paid' || order.estado === 'completed');

  const handleResendPurchaseConfirmation = async (orderId) => {
    if (!token || !selectedUser?.id || !orderId) return;
    setResendingOrderId(orderId);
    try {
      const response = await fetch(
        buildApiUrl(
          `/api/admin/users/${selectedUser.id}/orders/${orderId}/resend-purchase-confirmation`,
        ),
        { method: 'POST', headers: authHeaders },
      );
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.error || `Error ${response.status}`);
      }
      notify({
        title: 'Correo reenviado',
        message: body.message || 'Se envió la confirmación de compra al email del usuario.',
        variant: 'success',
      });
    } catch (error) {
      logError('[AdminUsuarios] reenvío confirmación compra:', error);
      notify({
        title: 'No se pudo reenviar',
        message: error.message || 'Error al enviar el correo.',
        variant: 'error',
      });
    } finally {
      setResendingOrderId(null);
    }
  };

  const ticketEstadoLabel = (estado) => {
    const map = {
      disponible: 'Disponible',
      vendido: 'Vendido',
      usado: 'Usado',
      cancelado: 'Cancelado',
    };
    return map[estado] || estado || '—';
  };

  /** Curso.estado en BD suele ser booleano */
  const formatCourseEstado = (v) => {
    if (v === true || v === 'true' || v === 1) return 'Activo';
    if (v === false || v === 'false' || v === 0) return 'Inactivo';
    if (v == null || v === '') return '—';
    return String(v);
  };

  if (!user) return null;

  return (
    <div className="admin-dashboard">
      <div className="admin-layout">
        <AdminSidebar
          activeKey="users"
          onNavigate={(item) => {
            if (item.path) {
              navigate(item.path);
              return;
            }
            notify({
              title: 'Sección en desarrollo',
              message: 'Estamos migrando esta sección al nuevo panel.',
              variant: 'info',
            });
          }}
        />
        <main className="admin-main">
          <div className="AUP-container">
            {/* Header */}
            <div className="AUP-header">
              <div className="AUP-header-content">
                <h1 className="AUP-title">
                  <i className="fas fa-users" />
                  Gestión de Usuarios
                </h1>
                <p className="AUP-subtitle">
                  Administra usuarios y sus permisos del sistema
                </p>
              </div>
            </div>

            {/* Búsqueda y Filtros */}
            <div className="AUP-search-section">
              <div className="AUP-search-container">
                <div className="AUP-search-input-group">
                  <i className="fas fa-search AUP-search-icon" />
                  <Form.Control
                    type="text"
                    placeholder="Buscar por nombre, email o ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="AUP-search-input"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      className="AUP-search-clear"
                      onClick={() => setSearchTerm('')}
                    >
                      <i className="fas fa-times" />
                    </button>
                  )}
                </div>
                <div className="AUP-search-filters">
                  <Form.Select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className="AUP-filter-select"
                  >
                    <option value="">Todos los roles</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.nombre}>
                        {role.nombre}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="AUP-filter-select"
                  >
                    <option value="">Todos los estados</option>
                    <option value="active">Activo</option>
                    <option value="inactive">Inactivo</option>
                    <option value="pending">Pendiente</option>
                  </Form.Select>
                </div>
              </div>
            </div>

            {/* Estadísticas */}
            <div className="AUP-stats-section">
              <div className="AUP-stats-grid">
                <Card className="AUP-stat-card">
                  <Card.Body>
                    <div className="AUP-stat-content">
                      <div className="AUP-stat-icon AUP-stat-icon-primary">
                        <i className="fas fa-users" />
                      </div>
                      <div className="AUP-stat-info">
                        <div className="AUP-stat-value">{stats.totalUsers}</div>
                        <div className="AUP-stat-label">Total Usuarios</div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
                <Card className="AUP-stat-card">
                  <Card.Body>
                    <div className="AUP-stat-content">
                      <div className="AUP-stat-icon AUP-stat-icon-success">
                        <i className="fas fa-user-shield" />
                      </div>
                      <div className="AUP-stat-info">
                        <div className="AUP-stat-value">{stats.adminUsers}</div>
                        <div className="AUP-stat-label">Administradores</div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
                <Card className="AUP-stat-card">
                  <Card.Body>
                    <div className="AUP-stat-content">
                      <div className="AUP-stat-icon AUP-stat-icon-info">
                        <i className="fas fa-wifi" />
                      </div>
                      <div className="AUP-stat-info">
                        <div className="AUP-stat-value">{stats.onlineUsers}</div>
                        <div className="AUP-stat-label">Conectados Ahora</div>
                        <div className="AUP-online-status">
                          <span className="AUP-status-dot" />
                          <span className="AUP-status-text">En tiempo real</span>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </div>

            {/* Tabla de Usuarios */}
            <div className="AUP-table-section">
              <div className="AUP-table-header">
                <h2 className="AUP-table-title">Lista de Usuarios</h2>
                <div className="AUP-table-actions">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={loadUsers}
                    disabled={loading}
                  >
                    <i className="fas fa-sync-alt me-2" />
                    Actualizar
                  </Button>
                </div>
              </div>

              <div className="AUP-table-container">
                {loading ? (
                  <div className="AUP-loading">
                    <Spinner animation="border" />
                    <p>Cargando usuarios...</p>
                  </div>
                ) : (
                  <Table responsive className="AUP-users-table">
                    <thead>
                      <tr>
                        <th>
                          <Form.Check
                            type="checkbox"
                            checked={
                              selectedUsers.length === paginatedUsers.length &&
                              paginatedUsers.length > 0
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedUsers(paginatedUsers.map((u) => u.id));
                              } else {
                                setSelectedUsers([]);
                              }
                            }}
                          />
                        </th>
                        <th>Usuario</th>
                        <th>Email</th>
                        <th>Rol</th>
                        <th>Estado</th>
                        <th>Último Acceso</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedUsers.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="AUP-empty-table">
                            <div className="AUP-empty-state">
                              <i className="fas fa-users" />
                              <p>No se encontraron usuarios</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        paginatedUsers.map((userItem) => (
                          <tr key={userItem.id}>
                            <td>
                              <Form.Check
                                type="checkbox"
                                checked={selectedUsers.includes(userItem.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedUsers([...selectedUsers, userItem.id]);
                                  } else {
                                    setSelectedUsers(
                                      selectedUsers.filter((id) => id !== userItem.id),
                                    );
                                  }
                                }}
                              />
                            </td>
                            <td>
                              <div className="AUP-user-cell">
                                <div className="AUP-user-avatar">
                                  {(userItem.name || userItem.nombre || 'U')
                                    .charAt(0)
                                    .toUpperCase()}
                                </div>
                                <div>
                                  <div className="AUP-user-name">
                                    {userItem.name || userItem.nombre || 'Sin nombre'}
                                  </div>
                                  <div className="AUP-user-id">ID: {userItem.id}</div>
                                </div>
                              </div>
                            </td>
                            <td>{userItem.email || '-'}</td>
                            <td>
                              <Badge bg="primary">
                                {getRoleDisplayName(userItem.role || userItem.rol)}
                              </Badge>
                            </td>
                            <td>{getStatusBadge(userItem.status || userItem.estado)}</td>
                            <td>{formatDate(userItem.lastAccess || userItem.ultimoAcceso)}</td>
                            <td>
                              <div className="AUP-actions">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleViewUser(userItem)}
                                >
                                  <i className="fas fa-eye" />
                                </Button>
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() => handleEditUser(userItem)}
                                >
                                  <i className="fas fa-edit" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                )}
              </div>

              {/* Paginación */}
              {totalPages > 1 && (
                <div className="AUP-pagination">
                  <div className="AUP-pagination-info">
                    Mostrando {paginatedUsers.length} de {filteredUsers.length} usuarios
                  </div>
                  <div className="AUP-pagination-controls">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      <i className="fas fa-chevron-left me-1" />
                      Anterior
                    </Button>
                    <div className="AUP-pagination-numbers">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(
                          (page) =>
                            page === 1 ||
                            page === totalPages ||
                            (page >= currentPage - 1 && page <= currentPage + 1),
                        )
                        .map((page, idx, arr) => (
                          <span key={page}>
                            {idx > 0 && arr[idx - 1] !== page - 1 && <span>...</span>}
                            <Button
                              variant={currentPage === page ? 'primary' : 'outline-secondary'}
                              size="sm"
                              onClick={() => setCurrentPage(page)}
                            >
                              {page}
                            </Button>
                          </span>
                        ))}
                    </div>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      Siguiente
                      <i className="fas fa-chevron-right ms-1" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Modal Ver Usuario */}
      <Modal
        show={showViewUserModal}
        onHide={closeViewUserModal}
        size="xl"
        scrollable
        centered
        dialogClassName="AUP-user-modal-dialog"
        contentClassName="AUP-user-modal-content"
        className="AUP-view-user-modal"
      >
        <Modal.Header closeButton className="AUP-modal-view-header">
          <Modal.Title className="d-flex flex-column flex-md-row align-items-md-center gap-2 w-100 me-2">
            <span className="AUP-modal-title-row">
              <span className="AUP-modal-title-icon">
                <i className="fas fa-user-circle" />
              </span>
              <span>
                Detalles del usuario
                {selectedUser && (
                  <span className="AUP-modal-title-meta d-block d-md-inline fw-normal text-muted fs-6 mt-1 mt-md-0 ms-md-2">
                    #{selectedUser.id}
                    <span className="mx-1">·</span>
                    {selectedUser.email}
                  </span>
                )}
              </span>
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="AUP-modal-body-scroll">
          {selectedUser && (
            <>
              <Nav
                variant="pills"
                className="AUP-view-user-nav AUP-nav-segmented flex-wrap mb-4"
                activeKey={viewModalTab}
                onSelect={(k) => setViewModalTab(k || 'perfil')}
              >
                <Nav.Item>
                  <Nav.Link eventKey="perfil">
                    <i className="fas fa-id-card me-1" />
                    Perfil
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="compras">
                    <i className="fas fa-shopping-bag me-1" />
                    Compras y pagos
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="registros">
                    <i className="fas fa-clipboard-list me-1" />
                    Registros y actividad
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              {adminActivityLoading && (
                <div className="AUP-activity-loading text-center py-4">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Cargando historial del usuario…
                </div>
              )}

              {adminActivityError && (
                <Alert variant="danger" className="mb-3">
                  {adminActivityError}
                </Alert>
              )}

              {adminActivity?.sectionErrors &&
                Object.keys(adminActivity.sectionErrors).length > 0 && (
                  <Alert variant="warning" className="mb-3 small py-2">
                    Parte del historial no se pudo cargar (el resto sí):{' '}
                    <strong>{Object.keys(adminActivity.sectionErrors).join(', ')}</strong>
                  </Alert>
                )}

              {viewModalTab === 'perfil' && (
                <div className="AUP-user-details">
                  <div className="AUP-profile-hero">
                    <div className="AUP-avatar-circle AUP-avatar-circle--lg">
                      {(selectedUser.name || selectedUser.nombre || 'U')
                        .charAt(0)
                        .toUpperCase()}
                    </div>
                    <div className="AUP-profile-hero-text">
                      <h3 className="AUP-profile-headline">
                        {selectedUser.name || selectedUser.nombre || 'Sin nombre'}{' '}
                        {selectedUser.lastName || selectedUser.apellido || ''}
                      </h3>
                      <p className="AUP-profile-email mb-2">{selectedUser.email || '—'}</p>
                      <div className="d-flex flex-wrap gap-2 align-items-center">
                        <Badge bg="primary" className="AUP-badge-soft">
                          {getRoleDisplayName(selectedUser.role || selectedUser.rol)}
                        </Badge>
                        {getStatusBadge(selectedUser.status || selectedUser.estado)}
                      </div>
                    </div>
                  </div>

                  <div className="AUP-user-info-grid AUP-user-info-grid--cards">
                    <div className="AUP-info-item AUP-info-card">
                      <label>ID</label>
                      <span>{selectedUser.id}</span>
                    </div>
                    <div className="AUP-info-item AUP-info-card">
                      <label>Nombre</label>
                      <span>{selectedUser.name || selectedUser.nombre || '—'}</span>
                    </div>
                    <div className="AUP-info-item AUP-info-card">
                      <label>Apellido</label>
                      <span>{selectedUser.lastName || selectedUser.apellido || '—'}</span>
                    </div>
                    <div className="AUP-info-item AUP-info-card">
                      <label>Email</label>
                      <span className="AUP-text-break">{selectedUser.email || '—'}</span>
                    </div>
                    <div className="AUP-info-item AUP-info-card">
                      <label>Rol</label>
                      <div>
                        <Badge bg="primary" className="AUP-badge-soft">
                          {getRoleDisplayName(selectedUser.role || selectedUser.rol)}
                        </Badge>
                      </div>
                    </div>
                    <div className="AUP-info-item AUP-info-card">
                      <label>Estado en plataforma</label>
                      <div>{getStatusBadge(selectedUser.status || selectedUser.estado)}</div>
                    </div>
                    <div className="AUP-info-item AUP-info-card">
                      <label>Último acceso</label>
                      <span>
                        {formatDate(selectedUser.lastAccess || selectedUser.ultimoAcceso)}
                      </span>
                    </div>
                    <div className="AUP-info-item AUP-info-card">
                      <label>Alta en sistema</label>
                      <span>{formatDate(selectedUser.createdAt || selectedUser.created_at)}</span>
                    </div>
                    {adminActivity?.user && (
                      <>
                        <div className="AUP-info-item AUP-info-card">
                          <label>Términos aceptados</label>
                          <span>{adminActivity.user.aceptoTerminos ? 'Sí' : 'No'}</span>
                        </div>
                        <div className="AUP-info-item AUP-info-card">
                          <label>Suscrito a newsletter</label>
                          <span>{adminActivity.user.suscritoNewsletter ? 'Sí' : 'No'}</span>
                        </div>
                        {adminActivity.user.fechaRegistro && (
                          <div className="AUP-info-item AUP-info-card">
                            <label>Registro (legacy)</label>
                            <span>{formatDate(adminActivity.user.fechaRegistro)}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {adminActivity?.meta?.counts && (
                    <div className="AUP-stat-tiles" aria-label="Resumen de actividad">
                      {[
                        ['Órdenes', adminActivity.meta.counts.orders, 'fa-shopping-bag'],
                        ['Transacciones', adminActivity.meta.counts.transactions, 'fa-credit-card'],
                        ['Cursos', adminActivity.meta.counts.courses, 'fa-graduation-cap'],
                        ['Eventos', adminActivity.meta.counts.events, 'fa-calendar-check'],
                        ['Tickets', adminActivity.meta.counts.tickets, 'fa-ticket-alt'],
                        ['Asesorías', adminActivity.meta.counts.asesorias, 'fa-user-md'],
                      ].map(([label, value, icon]) => (
                        <div key={label} className="AUP-stat-tile">
                          <span className="AUP-stat-tile-icon" aria-hidden>
                            <i className={`fas ${icon}`} />
                          </span>
                          <span className="AUP-stat-tile-value">{value}</span>
                          <span className="AUP-stat-tile-label">{label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {viewModalTab === 'compras' && adminActivity && (
                <div className="AUP-activity-tab">
                  <div className="AUP-section-head">
                    <span className="AUP-section-head-icon">
                      <i className="fas fa-receipt" />
                    </span>
                    <div>
                      <h6 className="AUP-section-title mb-0">Órdenes de compra</h6>
                      <p className="AUP-section-sub text-muted small mb-0">
                        Historial de pedidos e ítems facturados
                      </p>
                    </div>
                  </div>
                  {!adminActivity.purchases?.length ? (
                    <div className="AUP-empty-panel text-muted small">
                      No hay órdenes registradas para este usuario.
                    </div>
                  ) : (
                    <div className="AUP-purchase-list w-100">
                      {adminActivity.purchases.map((order) => (
                        <Card key={order.id} className="AUP-purchase-card AUP-purchase-card--enhanced border-0 w-100">
                          <Card.Header className="AUP-purchase-head border-0">
                            <div className="AUP-purchase-head-left">
                              <div className="AUP-purchase-head-title-row">
                                <span className="AUP-purchase-id">Orden #{order.id}</span>
                                {order.items?.length > 0 && (
                                  <span className="AUP-purchase-chip">
                                    {order.items.length}{' '}
                                    {order.items.length === 1 ? 'ítem' : 'ítems'}
                                  </span>
                                )}
                              </div>
                              <code className="AUP-order-number">{order.orderNumber}</code>
                            </div>
                            <div className="AUP-purchase-head-right">
                              <Badge
                                bg={orderStatusVariant(order.estado)}
                                className="AUP-badge-soft px-3 py-2"
                              >
                                {orderStatusLabel(order.estado)}
                              </Badge>
                              <span className="AUP-purchase-total">
                                {formatCurrency(order.total, order.currency)}
                              </span>
                            </div>
                          </Card.Header>
                          <Card.Body className="AUP-purchase-body AUP-purchase-body--three pt-0">
                            <div className="AUP-order-meta-col">
                              <dl className="AUP-order-meta AUP-order-meta--horizontal">
                                <div className="AUP-order-meta-row">
                                  <dt>Fecha</dt>
                                  <dd>
                                    <i className="far fa-calendar-alt me-1 text-muted" />
                                    {formatDate(order.fecha)}
                                  </dd>
                                </div>
                                {order.completedAt && (
                                  <div className="AUP-order-meta-row">
                                    <dt>Pago completado</dt>
                                    <dd>{formatDate(order.completedAt)}</dd>
                                  </div>
                                )}
                                {order.paymentMethod && (
                                  <div className="AUP-order-meta-row">
                                    <dt>Método de pago</dt>
                                    <dd>{order.paymentMethod}</dd>
                                  </div>
                                )}
                                {order.paymentId && (
                                  <div className="AUP-order-meta-row">
                                    <dt>ID pago (MP)</dt>
                                    <dd>
                                      <code className="AUP-code-tiny">{order.paymentId}</code>
                                    </dd>
                                  </div>
                                )}
                                {order.transaction && (
                                  <>
                                    <div className="AUP-order-meta-row">
                                      <dt>ID transacción</dt>
                                      <dd>
                                        <code className="AUP-code-tiny text-break">
                                          {order.transaction.transaction_id}
                                        </code>
                                      </dd>
                                    </div>
                                    <div className="AUP-order-meta-row">
                                      <dt>Estado en pasarela</dt>
                                      <dd>
                                        <span className="fw-semibold">{order.transaction.status}</span>
                                        <span className="text-muted mx-1">·</span>
                                        {formatCurrency(
                                          order.transaction.amount,
                                          order.transaction.currency,
                                        )}
                                      </dd>
                                    </div>
                                  </>
                                )}
                                {order.subtotal != null && Number(order.subtotal) > 0 && (
                                  <div className="AUP-order-meta-row">
                                    <dt>Subtotal</dt>
                                    <dd>{formatCurrency(order.subtotal, order.currency)}</dd>
                                  </div>
                                )}
                                {order.discount != null && Number(order.discount) > 0 && (
                                  <div className="AUP-order-meta-row">
                                    <dt>Descuento</dt>
                                    <dd className="text-success">
                                      −{formatCurrency(order.discount, order.currency)}
                                    </dd>
                                  </div>
                                )}
                                {order.tax != null && Number(order.tax) > 0 && (
                                  <div className="AUP-order-meta-row">
                                    <dt>Impuestos</dt>
                                    <dd>{formatCurrency(order.tax, order.currency)}</dd>
                                  </div>
                                )}
                              </dl>
                            </div>
                            <div className="AUP-order-items-col">
                              <div className="AUP-order-items-label">Productos</div>
                              {order.items?.length > 0 ? (
                                <div className="AUP-order-items-grid">
                                  {order.items.map((item) => (
                                    <div key={item.id} className="AUP-order-line-card">
                                      <div className="AUP-order-line-main">
                                        <div className="AUP-order-line-title">
                                          {item.product?.name || item.bundle?.nombre || '—'}
                                        </div>
                                        <div className="AUP-order-line-meta text-muted small">
                                          {item.bundle ? (
                                            <span>Bundle</span>
                                          ) : (
                                            <span className="text-capitalize">
                                              {item.product?.type || item.itemType || '—'}
                                            </span>
                                          )}
                                          {item.product?.sku && (
                                            <span className="ms-2">· SKU {item.product.sku}</span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="AUP-order-line-stats">
                                        <span className="AUP-order-line-stat">
                                          <em>Cant.</em>
                                          {item.quantity}
                                        </span>
                                        <span className="AUP-order-line-stat">
                                          <em>P. unit.</em>
                                          {formatCurrency(item.unitPrice, order.currency)}
                                        </span>
                                        <span className="AUP-order-line-stat AUP-order-line-stat--total">
                                          <em>Subtotal</em>
                                          <strong>{formatCurrency(item.totalPrice, order.currency)}</strong>
                                        </span>
                                      </div>
                                      {(item.fulfillmentStatus || item.trackingNumber) && (
                                        <div className="AUP-order-line-foot small text-secondary">
                                          <span className="fw-semibold text-uppercase small text-muted me-1">
                                            Envío:
                                          </span>
                                          {item.fulfillmentStatus || '—'}
                                          {item.trackingNumber && (
                                            <span className="ms-2 text-dark">
                                              Tracking: {item.trackingNumber}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-muted small mb-0">Sin líneas de detalle en esta orden.</p>
                              )}
                            </div>
                            <div className="AUP-purchase-actions-col">
                              <div className="AUP-purchase-totals-strip">
                                {order.subtotal != null && Number(order.subtotal) > 0 && (
                                  <div className="AUP-purchase-totals-strip__row">
                                    <span>Subtotal</span>
                                    <span>{formatCurrency(order.subtotal, order.currency)}</span>
                                  </div>
                                )}
                                {order.discount != null && Number(order.discount) > 0 && (
                                  <div className="AUP-purchase-totals-strip__row text-success">
                                    <span>Descuento</span>
                                    <span>−{formatCurrency(order.discount, order.currency)}</span>
                                  </div>
                                )}
                                {order.tax != null && Number(order.tax) > 0 && (
                                  <div className="AUP-purchase-totals-strip__row">
                                    <span>Impuestos</span>
                                    <span>{formatCurrency(order.tax, order.currency)}</span>
                                  </div>
                                )}
                                <div className="AUP-purchase-totals-strip__row AUP-purchase-totals-strip__row--grand">
                                  <span>Total</span>
                                  <span>{formatCurrency(order.total, order.currency)}</span>
                                </div>
                              </div>
                              {canResendPurchaseConfirmation(order) ? (
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="AUP-btn-resend-mail w-100"
                                  disabled={resendingOrderId === order.id}
                                  onClick={() => handleResendPurchaseConfirmation(order.id)}
                                >
                                  {resendingOrderId === order.id ? (
                                    <>
                                      <Spinner animation="border" size="sm" className="me-2" />
                                      Enviando…
                                    </>
                                  ) : (
                                    <>
                                      <i className="fas fa-envelope me-2" />
                                      Reenviar confirmación
                                    </>
                                  )}
                                </Button>
                              ) : (
                                <p className="AUP-purchase-mail-hint small text-muted mb-0">
                                  El reenvío del mail solo está disponible para órdenes pagadas o
                                  completadas.
                                </p>
                              )}
                              {canResendPurchaseConfirmation(order) && (
                                <p className="AUP-purchase-mail-hint small text-muted mb-0">
                                  Envía de nuevo el correo de confirmación al email del usuario (plantilla
                                  actual del sistema).
                                </p>
                              )}
                            </div>
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  )}

                  <div className="AUP-section-head mt-5">
                    <span className="AUP-section-head-icon">
                      <i className="fas fa-credit-card" />
                    </span>
                    <div>
                      <h6 className="AUP-section-title mb-0">Transacciones de pago</h6>
                      <p className="AUP-section-sub text-muted small mb-0">
                        Movimientos registrados en la pasarela
                      </p>
                    </div>
                  </div>
                  {!adminActivity.transactions?.length ? (
                    <div className="AUP-empty-panel text-muted small">
                      No hay transacciones registradas.
                    </div>
                  ) : (
                    <div className="AUP-transactions-panel">
                      <div className="AUP-table-scroll">
                        <Table responsive className="mb-0 AUP-data-table AUP-nested-table">
                          <thead>
                            <tr>
                              <th>Fecha</th>
                              <th>Orden</th>
                              <th>ID transacción</th>
                              <th>Estado</th>
                              <th>Método</th>
                              <th className="text-end">Importe</th>
                            </tr>
                          </thead>
                          <tbody>
                            {adminActivity.transactions.map((tx) => (
                              <tr key={tx.id}>
                                <td className="text-nowrap">{formatDate(tx.created_at)}</td>
                                <td>
                                  <code className="AUP-code-inline">{tx.order_id}</code>
                                </td>
                                <td>
                                  <code className="AUP-code-inline text-break">{tx.transaction_id}</code>
                                </td>
                                <td>
                                  <span className="AUP-tx-status">{tx.status}</span>
                                </td>
                                <td>{tx.payment_method}</td>
                                <td className="text-end fw-medium text-nowrap">
                                  {formatCurrency(tx.amount, tx.currency)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {viewModalTab === 'compras' && !adminActivity && !adminActivityLoading && !adminActivityError && (
                <p className="text-muted small">Los datos de compras aparecerán al cargar el historial.</p>
              )}

              {viewModalTab === 'registros' && adminActivity && (
                <div className="AUP-activity-tab">
                  <section className="AUP-reg-block">
                    <div className="AUP-section-head">
                      <span className="AUP-section-head-icon">
                        <i className="fas fa-graduation-cap" />
                      </span>
                      <div>
                        <h6 className="AUP-section-title mb-0">Cursos asignados</h6>
                        <p className="AUP-section-sub text-muted small mb-0">
                          Accesos otorgados en la plataforma
                        </p>
                      </div>
                    </div>
                    {!adminActivity.courses?.length ? (
                      <div className="AUP-empty-panel text-muted small">Sin cursos vinculados.</div>
                    ) : (
                      <div className="AUP-table-scroll">
                        <Table className="mb-0 AUP-data-table AUP-nested-table">
                          <thead>
                            <tr>
                              <th>Curso</th>
                              <th>Estado curso</th>
                              <th>Asignado el</th>
                            </tr>
                          </thead>
                          <tbody>
                            {adminActivity.courses.map((c) => (
                              <tr key={`${c.courseId}-${c.acquiredAt}`}>
                                <td>
                                  <div className="AUP-cell-title">{c.titulo || `ID ${c.courseId}`}</div>
                                  {c.descripcionBreve && (
                                    <div className="text-muted small AUP-desc-clamp">{c.descripcionBreve}</div>
                                  )}
                                </td>
                                <td>
                                  <span
                                    className={`AUP-pill-status ${
                                      c.estadoCurso === true || c.estadoCurso === 1
                                        ? 'AUP-pill-status--ok'
                                        : 'AUP-pill-status--off'
                                    }`}
                                  >
                                    {formatCourseEstado(c.estadoCurso)}
                                  </span>
                                </td>
                                <td className="text-nowrap text-secondary small">
                                  {formatDate(c.acquiredAt)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </section>

                  <section className="AUP-reg-block">
                    <div className="AUP-section-head">
                      <span className="AUP-section-head-icon">
                        <i className="fas fa-calendar-check" />
                      </span>
                      <div>
                        <h6 className="AUP-section-title mb-0">Inscripciones a eventos</h6>
                        <p className="AUP-section-sub text-muted small mb-0">
                          Registros y códigos asignados
                        </p>
                      </div>
                    </div>
                    {!adminActivity.eventRegistrations?.length ? (
                      <div className="AUP-empty-panel text-muted small">Sin inscripciones a eventos.</div>
                    ) : (
                      <div className="AUP-table-scroll">
                        <Table className="mb-0 AUP-data-table AUP-nested-table">
                          <thead>
                            <tr>
                              <th>Evento</th>
                              <th>Formato</th>
                              <th>Inscripción</th>
                              <th>Códigos</th>
                            </tr>
                          </thead>
                          <tbody>
                            {adminActivity.eventRegistrations.map((ev) => (
                              <tr key={`${ev.eventId}-${ev.acquiredAt}`}>
                                <td>
                                  <div className="AUP-cell-title">{ev.titulo}</div>
                                  {ev.direccion && (
                                    <div className="text-muted small">{ev.direccion}</div>
                                  )}
                                </td>
                                <td>
                                  <span className="AUP-format-badge">{ev.formato || '—'}</span>
                                </td>
                                <td className="text-nowrap text-secondary small">
                                  {formatDate(ev.acquiredAt)}
                                </td>
                                <td>
                                  {!ev.codigos?.length ? (
                                    '—'
                                  ) : (
                                    <ul className="AUP-code-list mb-0">
                                      {ev.codigos.map((co) => (
                                        <li key={co.id}>
                                          <span className="AUP-code-pill">{co.code}</span>
                                          <span className="text-muted small ms-1">({co.status})</span>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </section>

                  <section className="AUP-reg-block">
                    <div className="AUP-section-head">
                      <span className="AUP-section-head-icon">
                        <i className="fas fa-ticket-alt" />
                      </span>
                      <div>
                        <h6 className="AUP-section-title mb-0">Tickets de evento</h6>
                        <p className="AUP-section-sub text-muted small mb-0">
                          Entradas vinculadas al usuario
                        </p>
                      </div>
                    </div>
                    {!adminActivity.eventTickets?.length ? (
                      <div className="AUP-empty-panel text-muted small">Sin tickets asociados.</div>
                    ) : (
                      <div className="AUP-table-scroll">
                        <Table className="mb-0 AUP-data-table AUP-nested-table">
                          <thead>
                            <tr>
                              <th>Código ticket</th>
                              <th>Evento</th>
                              <th>Estado</th>
                              <th>Compra</th>
                              <th>Uso</th>
                            </tr>
                          </thead>
                          <tbody>
                            {adminActivity.eventTickets.map((tk) => (
                              <tr key={tk.id}>
                                <td>
                                  <span className="AUP-code-pill">{tk.codigo}</span>
                                </td>
                                <td>
                                  <div className="AUP-cell-title">{tk.evento?.nombre || '—'}</div>
                                </td>
                                <td>{ticketEstadoLabel(tk.estado)}</td>
                                <td className="text-nowrap text-secondary small">
                                  {formatDate(tk.fechaCompra)}
                                </td>
                                <td className="text-nowrap text-secondary small">
                                  {formatDate(tk.fechaUso)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </section>

                  <section className="AUP-reg-block">
                    <div className="AUP-section-head">
                      <span className="AUP-section-head-icon">
                        <i className="fas fa-user-md" />
                      </span>
                      <div>
                        <h6 className="AUP-section-title mb-0">Asesorías reservadas</h6>
                        <p className="AUP-section-sub text-muted small mb-0">
                          Citas asignadas a este usuario
                        </p>
                      </div>
                    </div>
                    {!adminActivity.asesorias?.length ? (
                      <div className="AUP-empty-panel text-muted small">
                        Sin asesorías asignadas a este usuario.
                      </div>
                    ) : (
                      <div className="AUP-table-scroll">
                        <Table className="mb-0 AUP-data-table AUP-nested-table">
                          <thead>
                            <tr>
                              <th>Fecha / hora</th>
                              <th>Profesional</th>
                              <th>Precio</th>
                              <th>Detalle</th>
                              <th>Videollamada</th>
                            </tr>
                          </thead>
                          <tbody>
                            {adminActivity.asesorias.map((a) => (
                              <tr key={a.id}>
                                <td className="text-nowrap small">
                                  <div>{formatDate(a.start_time)}</div>
                                  {a.end_time && (
                                    <div className="text-muted">Fin: {formatDate(a.end_time)}</div>
                                  )}
                                </td>
                                <td>
                                  {a.profesor
                                    ? `${a.profesor.nombre || ''} ${a.profesor.apellido || ''}`.trim()
                                    : '—'}
                                </td>
                                <td className="fw-medium text-nowrap">{formatCurrency(a.Precio)}</td>
                                <td className="small text-secondary AUP-desc-clamp-wide">{a.text || '—'}</td>
                                <td>{a.tieneVideollamada ? 'Sí' : 'No'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </section>
                </div>
              )}

              {viewModalTab === 'registros' && !adminActivity && !adminActivityLoading && !adminActivityError && (
                <p className="text-muted small">Los registros aparecerán al cargar el historial.</p>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="AUP-modal-view-footer border-0 pt-0">
          <Button
            variant="outline-secondary"
            className="AUP-btn-ghost"
            onClick={closeViewUserModal}
          >
            Cerrar
          </Button>
          <Button
            variant="primary"
            className="AUP-btn-primary-solid px-4"
            onClick={() => {
              closeViewUserModal();
              handleEditUser(selectedUser);
            }}
          >
            <i className="fas fa-edit me-2" />
            Editar usuario
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Editar Usuario */}
      <Modal show={showEditUserModal} onHide={() => setShowEditUserModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Editar Usuario</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nombre *</Form.Label>
              <Form.Control
                type="text"
                value={userForm.nombre}
                onChange={(e) => setUserForm({ ...userForm, nombre: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Apellido</Form.Label>
              <Form.Control
                type="text"
                value={userForm.apellido}
                onChange={(e) => setUserForm({ ...userForm, apellido: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email *</Form.Label>
              <Form.Control
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Rol</Form.Label>
              <Form.Select
                value={userForm.role}
                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
              >
                <option value="">Seleccionar rol...</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.nombre}>
                    {role.nombre}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Estado</Form.Label>
              <Form.Select
                value={userForm.status}
                onChange={(e) => setUserForm({ ...userForm, status: e.target.value })}
              >
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
                <option value="pending">Pendiente</option>
                <option value="suspended">Suspendido</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowEditUserModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveUser}>
            <i className="fas fa-save me-2" />
            Guardar Cambios
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const AdminUsuariosPage = () => (
  <AdminNotificationsProvider>
    <AdminUsuariosPageInner />
  </AdminNotificationsProvider>
);

export default AdminUsuariosPage;
